import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Check, ArrowRight } from 'lucide-react';
import { submitInquiry, fetchCategories } from '@/lib/api';
import { trackEvent } from '@/lib/analytics';
import { INQUIRY } from '@/constants/testIds';
import useReveal from '@/hooks/useReveal';

const VOLUME_OPTIONS = [
  { value: '1-50kg / week',    label: '1 – 50 kg / week',    hint: 'Café · Small restaurant · Boutique retail' },
  { value: '50-500kg / week',  label: '50 – 500 kg / week',  hint: 'Restaurant group · Supermarket line · Butcher shop' },
  { value: '500kg+ / week',    label: '500 kg+ / week',      hint: 'Hospitality group · Chain · Manufacturer · Export' },
];

const initial = {
  business_name: '',
  contact_name: '',
  email: '',
  phone: '',
  estimated_volume: '',
  categories: [],
  message: '',
};

export default function Inquiry() {
  const reveal = useReveal();
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    fetchCategories().then(setCategories).catch(() => {});
  }, []);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const toggleCategory = (slug) =>
    setForm((f) => ({
      ...f,
      categories: f.categories.includes(slug)
        ? f.categories.filter((c) => c !== slug)
        : [...f.categories, slug],
    }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await submitInquiry(form);
      trackEvent('Inquiry Submitted', {
        source: form.source || 'general',
        categories: (form.categories || []).join(',') || 'none',
      });
      setSuccess(true);
      setForm(initial);
      toast.success('Inquiry received. We will be in touch shortly.');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail[0]?.msg : detail;
      toast.error(msg || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid={INQUIRY.section}
      className="relative py-24 sm:py-32 bg-[var(--mw-surface)] border-t border-white/10"
    >
      <div className="max-w-screen-2xl mx-auto px-6 sm:px-10 lg:px-16 grid lg:grid-cols-12 gap-12 lg:gap-20">
        <div ref={reveal} className="mw-reveal lg:col-span-5">
          <div className="flex items-center gap-3 mb-4">
            <span className="block w-10 h-px bg-white/40" />
            <span className="text-[10px] uppercase tracking-[0.32em] text-white/55">
              Wholesale · 005
            </span>
          </div>
          <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl text-white leading-[0.95] tracking-tight">
            Request a <em className="not-italic text-[var(--mw-primary)]">trade quote</em>.
          </h2>
          <p className="mt-6 text-white/65 leading-relaxed max-w-md">
            Tell us about your venue and the cuts you need. Our team will reply
            with availability, pricing and delivery scheduling within one
            business day.
          </p>

          <div className="mt-12 space-y-6 text-sm text-white/70">
            <div>
              <div className="text-[10px] uppercase tracking-[0.28em] text-white/45">Cutting Room</div>
              <div className="mt-1 font-display text-xl text-white">15 Action Rd, Malaga WA 6090</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.28em] text-white/45">Trade Hours</div>
              <div className="mt-1">Mon–Fri · 6:00am – 4:00pm</div>
              <div>Sat · 7:00am – 12:00pm</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.28em] text-white/45">Direct</div>
              <a href="tel:+61435111667" className="mt-1 block font-display text-xl text-white hover:text-[var(--mw-primary)] transition-colors">
                0435 111 667
              </a>
              <a href="mailto:trade@themeatworks.com.au" className="hover:text-white transition-colors">
                trade@themeatworks.com.au
              </a>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          {success ? (
            <div
              data-testid={INQUIRY.success}
              className="border border-[var(--mw-primary)]/40 bg-black/40 p-10 sm:p-14 flex flex-col items-start"
            >
              <div className="w-14 h-14 rounded-full bg-[var(--mw-primary)] flex items-center justify-center">
                <Check size={24} className="text-white" />
              </div>
              <h3 className="font-display text-3xl sm:text-4xl text-white mt-6">
                Inquiry received.
              </h3>
              <p className="mt-4 text-white/65 max-w-md leading-relaxed">
                Our wholesale team will be in touch within one business day with
                availability, pricing and delivery options.
              </p>
              <button
                onClick={() => setSuccess(false)}
                data-testid="inquiry-reset"
                className="mt-8 inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-white hover:text-[var(--mw-primary)] transition-colors"
              >
                Submit another <ArrowRight size={14} />
              </button>
            </div>
          ) : (
            <form
              onSubmit={onSubmit}
              data-testid={INQUIRY.form}
              className="space-y-8"
              noValidate
            >
              <div className="grid sm:grid-cols-2 gap-x-8 gap-y-2">
                <Field label="Business name" required>
                  <input
                    type="text"
                    required
                    data-testid={INQUIRY.business}
                    className="mw-input"
                    placeholder="Your restaurant or business"
                    value={form.business_name}
                    onChange={set('business_name')}
                  />
                </Field>
                <Field label="Contact name" required>
                  <input
                    type="text"
                    required
                    data-testid={INQUIRY.name}
                    className="mw-input"
                    placeholder="Full name"
                    value={form.contact_name}
                    onChange={set('contact_name')}
                  />
                </Field>
                <Field label="Email" required>
                  <input
                    type="email"
                    required
                    data-testid={INQUIRY.email}
                    className="mw-input"
                    placeholder="you@business.com"
                    value={form.email}
                    onChange={set('email')}
                  />
                </Field>
                <Field label="Phone" required>
                  <input
                    type="tel"
                    required
                    data-testid={INQUIRY.phone}
                    className="mw-input"
                    placeholder="+61"
                    value={form.phone}
                    onChange={set('phone')}
                  />
                </Field>
              </div>

              <Field label="Estimated volume — helps us qualify your quote fast" required>
                <select
                  data-testid={INQUIRY.volume}
                  className="mw-input appearance-none cursor-pointer"
                  value={form.estimated_volume}
                  onChange={set('estimated_volume')}
                  required
                >
                  <option value="">Select a tier…</option>
                  {VOLUME_OPTIONS.map((v) => (
                    <option key={v.value} value={v.value} className="bg-[var(--mw-surface)]">
                      {v.label}
                    </option>
                  ))}
                </select>
                {form.estimated_volume && (
                  <div
                    data-testid="inquiry-volume-hint"
                    className="mt-2 text-[10px] uppercase tracking-[0.22em] text-white/50"
                  >
                    {VOLUME_OPTIONS.find((v) => v.value === form.estimated_volume)?.hint}
                  </div>
                )}
              </Field>

              <div>
                <div className="text-[10px] uppercase tracking-[0.28em] text-white/45 mb-4">
                  Categories of interest
                </div>
                <div className="flex flex-wrap gap-2">
                  {categories.map((c) => {
                    const active = form.categories.includes(c.slug);
                    return (
                      <button
                        type="button"
                        key={c.slug}
                        data-testid={INQUIRY.category(c.slug)}
                        onClick={() => toggleCategory(c.slug)}
                        className={`text-[11px] uppercase tracking-[0.22em] px-3.5 py-2 border transition-colors ${
                          active
                            ? 'bg-[var(--mw-primary)] border-[var(--mw-primary)] text-white'
                            : 'border-white/15 text-white/70 hover:border-white hover:text-white'
                        }`}
                      >
                        {c.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <Field label="Message" required>
                <textarea
                  required
                  rows={4}
                  data-testid={INQUIRY.message}
                  className="mw-input resize-none"
                  placeholder="Tell us about your menu, frequency, specific cuts…"
                  value={form.message}
                  onChange={set('message')}
                />
              </Field>

              <button
                type="submit"
                disabled={submitting}
                data-testid={INQUIRY.submit}
                className="inline-flex items-center gap-3 bg-[var(--mw-primary)] hover:bg-[var(--mw-primary-hover)] disabled:opacity-60 disabled:cursor-not-allowed text-white text-xs uppercase tracking-[0.22em] px-8 py-4 transition-colors"
              >
                {submitting ? (
                  <>
                    <Loader2 size={16} className="animate-spin" /> Sending
                  </>
                ) : (
                  <>
                    Send Inquiry <ArrowRight size={16} />
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block py-3">
      <span className="text-[10px] uppercase tracking-[0.28em] text-white/45">
        {label} {required && <span className="text-[var(--mw-primary)]">*</span>}
      </span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
